import {
  getDuplicatedCustomTaskOrder,
  getDuplicatedParentTaskUpdate,
  updateLinkedGoal,
  updateLinkedLifeArea,
  updateLinkedRelationships,
} from '@griply/services';
import { useCallback } from 'react';
import { useDuplicateTasksMutation } from 'shared/hooks/queries/use-duplicate-tasks-mutation';
import { useUpdateTaskMutation } from 'shared/hooks/queries/use-update-task-mutation';
import { useRelationships } from 'shared/hooks/use-relationships';
import { useUser } from 'shared/hooks/use-user';
import {
  getGoalDocById,
  getLifeAreaDocById,
  getTaskDocs,
  updateGoal,
  updateLifeArea,
  updateRelationshipReferences,
  updateUserSettings,
} from 'shared/services/backend-api';
import { ID } from 'shared/types/id';
import { Task } from 'shared/types/task';
import { chunkArray } from 'shared/utils/chunk-array';

import { useTaskById } from '../use-task-by-id';

export const useDuplicateTasks = (parentTaskId?: ID) => {
  const { mutateAsync: duplicateTasks } = useDuplicateTasksMutation();
  const { mutateAsync: updateTask } = useUpdateTaskMutation();
  const parentTask = useTaskById(parentTaskId);

  const relationships = useRelationships();
  const user = useUser();

  return useCallback(
    async (tasks: Task[]) => {
      if (!tasks.length) {
        return;
      }

      const allSubtaskIds = new Set<ID>();
      tasks.forEach((task) => {
        task.childIds?.forEach((childId) => allSubtaskIds.add(childId));
      });

      const subtasks = allSubtaskIds.size
        ? (
            await Promise.all(
              chunkArray(Array.from(allSubtaskIds), 30).map(
                async (chunkedSubTaskIds) =>
                  await getTaskDocs({ ids: chunkedSubTaskIds }),
              ),
            )
          ).flat(1)
        : [];

      // if a parentTask is provided, ensure all tasks have it as their parent
      const parentTaskParentIds = parentTask
        ? [...(parentTask.parentIds ?? []), parentTask.id]
        : undefined;
      const preparedTasks = parentTaskParentIds
        ? [...tasks, ...subtasks].map((task) => ({
            ...task,
            parentIds: parentTaskParentIds,
          }))
        : [...tasks, ...subtasks];

      const newTasks = await duplicateTasks(preparedTasks);

      const oldToNewTaskMap = new Map<ID, Task>();
      [...tasks, ...subtasks].forEach((oldTask, index) => {
        const newTask = newTasks[index];
        if (newTask) {
          oldToNewTaskMap.set(oldTask.id, newTask);
        }
      });

      const mainTaskPairs = tasks
        .filter((task) => !task.parentIds?.length)
        .map((oldTask) => {
          const newTask = oldToNewTaskMap.get(oldTask.id);
          if (!newTask) {
            return null;
          }
          return { oldTask, newTask };
        })
        .filter(
          (pair): pair is { oldTask: Task; newTask: Task } => pair !== null,
        );

      const updates: Promise<unknown>[] = [];

      if (mainTaskPairs.length) {
        updates.push(
          updateLinkedGoal({
            taskPairs: mainTaskPairs,
            getGoalDocById,
            updateGoal,
          }),
          updateLinkedLifeArea({
            taskPairs: mainTaskPairs,
            getLifeAreaDocById,
            updateLifeArea,
          }),
          updateLinkedRelationships({
            taskPairs: mainTaskPairs,
            relationships,
            updateRelationshipReferences,
          }),
        );

        const customTaskOrder = getDuplicatedCustomTaskOrder(
          user?.settings?.customTaskOrder,
          mainTaskPairs,
        );
        if (Object.keys(customTaskOrder).length) {
          updates.push(updateUserSettings({ customTaskOrder }));
        }
      }

      if (parentTask) {
        const tasksToAttach = tasks
          .map((task) => oldToNewTaskMap.get(task.id))
          .filter((task): task is Task => !!task);
        updates.push(
          updateTask(
            getDuplicatedParentTaskUpdate(
              parentTask,
              tasksToAttach,
              oldToNewTaskMap,
            ),
          ),
        );
      }

      await Promise.all(updates);
    },
    [duplicateTasks, parentTask, relationships, updateTask, user],
  );
};
